

import type { FilterDefinition, FilterMultiSelection, FilterValue, SupportedFilterValue, Predicate } from "@filter/filter.types";
import { FilterSelectionKind, createDefaultSelection } from "@filter/filter.types";
import { SimpleFilterSingle } from "./simple-filter-single";

export abstract class SimpleFilterMulti<TItem> extends SimpleFilterSingle<TItem, FilterMultiSelection> {
    protected createDefinition(): FilterDefinition<FilterMultiSelection> {
        return {
            ...super.createDefinition(),
            kind: FilterSelectionKind.Multi,
            defaultSelection: createDefaultSelection(FilterSelectionKind.Multi),
        };
    }

    protected normalize(value: SupportedFilterValue): FilterMultiSelection {
        if (!this.isFilterValues(value)) return createDefaultSelection(FilterSelectionKind.Multi);

        return {
            kind: FilterSelectionKind.Multi,
            values: value,
        };
    }

    public removeValue(value: FilterValue): void {
        this._selection.update((current) => ({
            ...current,
            values: current.values.filter((v) => v !== value),
        }));
    }

    public addToSelection(value: FilterValue): void {
        if (this._selection().values.includes(value)) {
            this.removeValue(value);
            return;
        }

        this._selection.update((current) => ({
            ...current,
            values: [...current.values, value],
        }));
    }

    private isFilterValues(value: SupportedFilterValue): value is FilterValue[] {
        return Array.isArray(value) && value.every((item) => typeof item === "string" || typeof item === "number" || item === null);
    }

    protected getPredicate(selection: FilterMultiSelection): Predicate<TItem> {
        return (item) => {
            const value = this.getValue(item);
            if (value !== undefined && selection.values.includes(value)) return true;
            if (!this.noneOption || !selection.values.includes(this.noneOption.value)) return false;

            const finalValue = !value ? value : this.getLabel(value);
            return finalValue === undefined && selection.values.includes(this.noneOption.value);
        };
    }
}
